import { useState, type FormEvent } from 'react';
import { requestPasswordReset, useSession } from '@portal/platform/session';
import { AppLink } from '../app-link';
import { useShellTexts } from '../i18n/use-shell-texts';
import { inputClass } from '../kit/form/input';
import { Button } from '../kit/button';
import { Alert, AuthCard, Field } from './auth-card';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Ask for a reset link. Always ends on the same "check your email" message, whether the address
 * exists or the call failed — the backend answers alike for both, so an address cannot be probed.
 */
export function ForgotPasswordPage() {
  const t = useShellTexts().forgotPassword;
  const invalid = useShellTexts().login.emailInvalid;
  const { platform } = useSession();
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const emailError = touched && !EMAIL.test(email.trim()) ? invalid : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!EMAIL.test(email.trim())) {
      return;
    }
    setSubmitting(true);
    try {
      await requestPasswordReset(platform.client, email.trim());
    } catch {
      // Same message either way; see above.
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  }

  return (
    <AuthCard title={t.title}>
      {done ? (
        <>
          <Alert tone="success">
            <strong className="block">{t.successTitle}</strong>
            {t.successMessage}
          </Alert>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <p className="mb-4 text-ink-muted">{t.subtitle}</p>
          <Field id="email" label={t.email} error={emailError} addon="@" hideLabel>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={inputClass}
              placeholder={t.emailPlaceholder}
              value={email}
              onChange={event => setEmail(event.target.value)}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? 'email-error' : undefined}
            />
          </Field>
          <Button type="submit" variant="primary" className="px-6" loading={submitting}>
            {submitting ? t.submitting : t.submit}
          </Button>
        </form>
      )}
      <p className="mt-6 mb-0">
        <AppLink href="/login" className="text-sm text-brand hover:text-brand-dark">
          {t.backToLogin}
        </AppLink>
      </p>
    </AuthCard>
  );
}
